import {
  type FragmentOf,
  graphql,
  type ItemIdentifier,
  type ResultOf,
  readFragment,
} from "@opensea/graphql"
import { create } from "zustand"
import { mutative } from "zustand-mutative"
import { isBulkActionEnabled } from "@/constants/constants"
import { useAcceptOffersFragment } from "../use-accept-offers.fragment"
import { useCancelItemsListingsFragment } from "../use-cancel-items-listings.fragment"
import { useListItemsFragment } from "../use-list-items.fragment"
import { useTransferItemsFragment } from "../use-transfer-items.fragment"
import { isItemTradableFragment } from "../validation/tradable"
import { MAX_ITEMS_FOR_GALLERY, MAX_ITEMS_FOR_SELECTION } from "./constants"
import { HideButtonFragment } from "./ProfileItemsActionBar/HideButton.fragment"

export const profileItemsSelectionFragment = graphql(
  `
    fragment profileItemsSelection on Item {
      id
      imageUrl
      chain {
        identifier
      }
      contractAddress
      tokenId
      bestOffer {
        __typename
      }
      bestListing {
        maker {
          address
        }
      }
      collection {
        slug
      }
      lowestListingForOwner(address: $address) {
        __typename
      }
      isFungible
      ...useTransferItems
      ...useAcceptOffers
      ...useListItems
      ...useCancelItemsListings
      ...isItemTradable
      ...HideButton
    }
  `,
  [
    useTransferItemsFragment,
    useAcceptOffersFragment,
    useListItemsFragment,
    isItemTradableFragment,
    useCancelItemsListingsFragment,
    HideButtonFragment,
  ],
)

type ProfileSelectionAction = "transact" | "gallery"

export type ProfileItemsSelectionStore = {
  action: ProfileSelectionAction
  // Temporarily here until we support ProfileCounts API
  isLoadingItems: boolean
  hasMoreItems: boolean
  // Used to hold all of the search results
  allItems: ResultOf<typeof profileItemsSelectionFragment>[]
  allItemsMap: Map<string, ResultOf<typeof profileItemsSelectionFragment>>

  selectedItemIds: string[]
  // Used to hold selected items but only includes values that have been loaded by search
  selectedItemsMap: Map<
    string,
    | (ResultOf<typeof profileItemsSelectionFragment> & { quantity: number })
    | undefined
  >
  // Used to hold selected items added from outside of search results and potentially not loaded by search
  addedSelectedItemsMap: Map<string, ItemIdentifier>

  setAction: (action: ProfileSelectionAction) => void
  setIsLoadingItems: (isLoadingItems: boolean) => void
  setHasMoreItems: (hasMoreItems: boolean) => void

  setAllItems: (
    itemKeys: FragmentOf<typeof profileItemsSelectionFragment>[],
  ) => void

  // Used when the user bulk selects all items
  selectAllItems: () => void
  isItemSelectable: (itemId: string) => boolean

  selectItem: (id: string, selectionState?: boolean) => void
  addSelectedItems: (items: (ItemIdentifier & { id: string })[]) => void

  // For featured shelf editing
  selectedShelfId: string | null
  setSelectedShelfId: (shelfId: string | null) => void

  updateSelectionQuantity: (id: string, quantity: number) => void
  updateItemSelections: () => void
  resetSelections: () => void
}

export const createProfileItemsSelectionStore = () => {
  return create<ProfileItemsSelectionStore>()(
    mutative((set, get) => ({
      action: "transact",
      isLoadingItems: false,
      hasMoreItems: true,
      allItems: [],
      allItemsMap: new Map(),
      selectedShelfId: null,
      selectedItemIds: [],
      selectedItemsMap: new Map(),
      addedSelectedItemsMap: new Map(),
      setAction: (action: ProfileSelectionAction) => {
        set(state => {
          const selection = get().selectedItemsMap
          // If we're going to gallery mode and selectedItems > MAX_ITEMS_FOR_GALLERY, trim selectedItems down to MAX_ITEMS_FOR_GALLERY
          if (action === "gallery" && selection.size > MAX_ITEMS_FOR_GALLERY) {
            const selectedItemsArray = Array.from(selection.keys())
            const selectedItemsToKeep = selectedItemsArray.slice(
              0,
              MAX_ITEMS_FOR_GALLERY,
            )
            const newSelectedItems = new Map(
              selectedItemsToKeep.map(itemId => [
                itemId,
                selection.get(itemId),
              ]),
            )
            state.selectedItemsMap = newSelectedItems
            state.selectedItemIds = Array.from(newSelectedItems.keys())
          }
          state.action = action
        })
      },
      setIsLoadingItems: (isLoadingItems: boolean) => {
        set({ isLoadingItems })
      },
      setHasMoreItems: (hasMoreItems: boolean) => {
        set({ hasMoreItems })
      },
      setAllItems: (
        itemKeys: FragmentOf<typeof profileItemsSelectionFragment>[],
      ) => {
        set(state => {
          const allItems = itemKeys.map(item =>
            readFragment(profileItemsSelectionFragment, item),
          )
          state.allItems = allItems
          state.allItemsMap = new Map(
            state.allItems.map(item => [item.id, item]),
          )

          // Update any selected items that previously had no data
          if (state.selectedItemIds.length) {
            let hasUpdates = false
            const newSelectedItemsMap = new Map(state.selectedItemsMap)

            for (const id of state.selectedItemIds) {
              // If we have a selected item with no data, but now have the data
              if (
                !state.selectedItemsMap.get(id) &&
                state.allItemsMap.has(id)
              ) {
                const item = state.allItemsMap.get(id)
                if (item) {
                  newSelectedItemsMap.set(id, { ...item, quantity: 1 })
                  hasUpdates = true
                }
              }
            }

            if (hasUpdates) {
              state.selectedItemsMap = newSelectedItemsMap
            }
          }
        })
      },

      isItemSelectable: (id: string) => {
        const selectedItems = get().selectedItemsMap
        const selectedItemsCount = selectedItems.size
        const item = get().allItemsMap.get(id)
        const action = get().action
        const maxItems =
          action === "gallery" ? MAX_ITEMS_FOR_GALLERY : MAX_ITEMS_FOR_SELECTION

        // If it's already selected, it's always selectable (for deselection)
        if (selectedItems.has(id)) {
          return true
        }

        // If we're at capacity, new items aren't selectable
        if (selectedItemsCount >= maxItems) {
          return false
        }

        // In gallery mode, we don't need to check bulk action restrictions
        if (action === "gallery") {
          return true
        }

        // For transact mode, check bulk action restrictions (if we have item data)
        if (item) {
          const bulkActionEnabled = isBulkActionEnabled(item.collection?.slug)
          if (!bulkActionEnabled) {
            // Check if we already have an item from this collection
            const hasItemFromSameCollection = Array.from(selectedItems.values())
              .filter(Boolean) // Filter out undefined values
              .some(
                selectedItem =>
                  item.collection?.slug === selectedItem?.collection?.slug,
              )

            if (hasItemFromSameCollection) {
              return false
            }
          }
        }

        return true
      },

      selectAllItems: () => {
        set(state => {
          if (state.allItems.length === 0) {
            return
          }

          if (state.selectedItemsMap.size > 0) {
            state.selectedItemsMap = new Map()
            state.selectedItemIds = []
          } else {
            const itemsToSelect = new Set()
            const selectedCollections = new Set()
            const maxItems =
              get().action === "gallery"
                ? MAX_ITEMS_FOR_GALLERY
                : MAX_ITEMS_FOR_SELECTION

            for (const item of state.allItems) {
              const hasSelectedItemFromCollection = selectedCollections.has(
                item.collection?.slug,
              )
              if (
                !isBulkActionEnabled(item.collection?.slug) &&
                hasSelectedItemFromCollection
              ) {
                continue
              }

              itemsToSelect.add(item.id)
              selectedCollections.add(item.collection?.slug)
            }

            const selectedItems = state.allItems
              .filter(item => itemsToSelect.has(item.id))
              .slice(0, maxItems)

            state.selectedItemsMap = new Map(
              selectedItems.map(item => [item.id, { ...item, quantity: 1 }]),
            )
            state.selectedItemIds = selectedItems.map(item => item.id)
          }
        })
      },

      selectItem: (id: string, selectionState?: boolean) => {
        set(state => {
          const item = state.allItemsMap.get(id)
          const maxItems =
            get().action === "gallery"
              ? MAX_ITEMS_FOR_GALLERY
              : MAX_ITEMS_FOR_SELECTION

          const addItem = () => {
            // Only add if not at max capacity
            if (state.selectedItemsMap.size >= maxItems) {
              return false
            }

            state.selectedItemsMap.set(
              id,
              item ? { ...item, quantity: 1 } : undefined,
            )
            state.selectedItemIds.push(id)
            return true
          }

          const removeItem = () => {
            state.selectedItemsMap.delete(id)
            state.selectedItemIds = state.selectedItemIds.filter(
              itemId => itemId !== id,
            )
            return true
          }

          // If selectionState is provided, explicitly set the selection state
          if (selectionState !== undefined) {
            // If trying to select and item is not already selected
            if (selectionState && !state.selectedItemsMap.has(id)) {
              addItem()
            }
            // If trying to deselect and item is currently selected
            else if (!selectionState && state.selectedItemsMap.has(id)) {
              removeItem()
            }
            return
          }

          // Toggle selection (existing behavior)
          if (state.selectedItemsMap.has(id)) {
            removeItem()
          } else {
            addItem()
          }
        })
      },

      addSelectedItems: (items: (ItemIdentifier & { id: string })[]) => {
        set(state => {
          const maxItems =
            get().action === "gallery"
              ? MAX_ITEMS_FOR_GALLERY
              : MAX_ITEMS_FOR_SELECTION

          // Clear existing selections
          state.selectedItemsMap = new Map()
          state.selectedItemIds = []

          // If adding all would exceed limit, only add up to the limit
          const itemsToAdd = items.slice(0, maxItems)

          // Add each item
          for (const item of itemsToAdd) {
            const itemData = state.allItemsMap.get(item.id)
            state.selectedItemsMap.set(
              item.id,
              itemData ? { ...itemData, quantity: 1 } : undefined,
            )
            state.selectedItemIds.push(item.id)
            state.addedSelectedItemsMap.set(item.id, item)
          }
        })
      },

      setSelectedShelfId: shelfId => {
        set({ selectedShelfId: shelfId })
      },

      updateSelectionQuantity: (id: string, quantity: number) => {
        set(state => {
          const item = state.selectedItemsMap.get(id)
          if (state.selectedItemsMap.has(id)) {
            if (!quantity) {
              state.selectedItemsMap.delete(id)
              state.selectedItemIds = state.selectedItemIds.filter(
                itemId => itemId !== id,
              )
            } else if (item) {
              state.selectedItemsMap.set(id, { ...item, quantity })
            }
          }
        })
      },

      updateItemSelections: () => {
        set(state => {
          if (state.selectedItemsMap.size) {
            const itemsToRemove: string[] = []
            const itemsToUpdate: string[] = []

            // Check items to remove or update
            for (const id of state.selectedItemIds) {
              const selectedItem = state.selectedItemsMap.get(id)

              // If item has data but its data no longer exists in allItemsMap, mark for removal
              if (selectedItem && !state.allItemsMap.has(id)) {
                itemsToRemove.push(id)
              }
              // If item doesn't have data but now exists in allItemsMap, mark for update
              else if (!selectedItem && state.allItemsMap.has(id)) {
                itemsToUpdate.push(id)
              }
            }

            // Apply changes if needed
            if (itemsToRemove.length || itemsToUpdate.length) {
              // Remove items that no longer exist in allItemsMap
              if (itemsToRemove.length) {
                for (const id of itemsToRemove) {
                  state.selectedItemsMap.delete(id)
                }
                state.selectedItemIds = state.selectedItemIds.filter(
                  id => !itemsToRemove.includes(id),
                )
              }

              // Update items that now have data
              if (itemsToUpdate.length) {
                for (const id of itemsToUpdate) {
                  const item = state.allItemsMap.get(id)
                  if (item) {
                    state.selectedItemsMap.set(id, { ...item, quantity: 1 })
                  }
                }
              }
            }
          }
        })
      },

      resetSelections: () => {
        set({
          selectedItemsMap: new Map(),
          selectedItemIds: [],
          addedSelectedItemsMap: new Map(),
        })
      },
    })),
  )
}
